import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Phone, ArrowRight } from "lucide-react";
import { API, PHONE, PHONE_TEL } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* retry */
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "polling" && (
        <>
          <Loader2 size={48} className="animate-spin text-terracotta" />
          <h1 className="display-hero mt-8 text-4xl">Confirmation du paiement…</h1>
          <p className="mt-4 text-sm text-espresso/60">Quelques secondes suffisent.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={56} strokeWidth={1.25} className="text-terracotta" />
          <h1 className="display-hero mt-8 text-4xl sm:text-5xl" data-testid="payment-confirmed-title">
            Votre brunch est réservé.
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-espresso/70">
            Paiement confirmé, créneau bloqué, atelier prévenu. Vous recevrez un email
            de récapitulatif dans quelques instants. Il ne vous reste qu'à préparer la table.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/" data-testid="payment-success-home" className="btn-primary">Retour à l'accueil</Link>
            <a href={PHONE_TEL} data-testid="payment-success-phone" className="btn-ghost">
              <Phone size={16} strokeWidth={1.5} /> {PHONE}
            </a>
          </div>
        </>
      )}
      {(state === "timeout" || state === "error") && (
        <>
          <XCircle size={56} strokeWidth={1.25} className="text-terracotta" />
          <h1 className="display-hero mt-8 text-4xl">Vérification en cours</h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-espresso/70">
            Si votre paiement a bien été débité, votre commande est enregistrée.
            Contactez-nous au {PHONE} pour confirmation immédiate.
          </p>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-6 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle size={56} strokeWidth={1.25} className="text-clay" />
      <h1 className="display-hero mt-8 text-4xl sm:text-5xl">Paiement interrompu.</h1>
      <p className="mt-6 max-w-md text-base leading-relaxed text-espresso/70">
        Aucun montant n'a été débité et votre créneau n'est pas réservé.
        Vous pouvez reprendre votre commande quand vous le souhaitez.
      </p>
      <Link to="/commander" data-testid="payment-cancel-retry" className="btn-primary mt-10">
        Reprendre ma commande <ArrowRight size={16} strokeWidth={1.5} />
      </Link>
    </div>
  );
}
